/**
 * AnnouncementApprovalDialog — used by the Universal Approval Requests bell
 * (`EditAccessRequest.jsx`) so that when an approver clicks an
 * `announcements` request the FULL native announcement detail is shown
 * (title, body, priority, audience, attachment) with in-modal
 * Approve / Reject buttons that call the announcement endpoints. This
 * mirrors the module's own DetailDialog so approvers see the same context
 * they'd see inside the Announcements module.
 */
import { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import { fmtDateTimeIST } from "../lib/datetime";
import {
  AlertTriangle, CheckCircle2, XCircle, Download, ExternalLink,
  Users, Megaphone, Zap,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
  draft:    "bg-slate-100 text-slate-600",
  approved: "bg-emerald-100 text-emerald-800",
  pending:  "bg-amber-100 text-amber-800",
  rejected: "bg-rose-100 text-rose-800",
  scheduled:"bg-sky-100 text-sky-800",
  expired:  "bg-slate-200 text-slate-700",
};

const ROLE_LABEL = {
  master_admin: "Master Admin", hr_admin: "HR Admin", sales_admin: "Sales Admin",
  sales_team: "Sales Team", accounts_admin: "Accounts Admin",
  dispatch_admin: "Dispatch Admin", it_admin: "IT Admin",
  crm_manager: "CRM Manager", documentation_team: "Documentation",
  trainer: "Trainer", employee: "Employee",
};
const roleLabel = (r) => ROLE_LABEL[r] || r || "—";

export default function AnnouncementApprovalDialog({
  open,
  onClose,
  announcementId,
  accessRole,    // 'master_admin' | 'admin' | 'editor' | null
  onActionDone,  // called after approve/reject success (parent refresh)
}) {
  const [ann, setAnn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !announcementId) return;
    let cancelled = false;
    setLoading(true);
    setAnn(null);
    setRejectMode(false);
    setReason("");
    (async () => {
      try {
        const { data } = await api.get(`/announcements/${announcementId}`);
        if (!cancelled) setAnn(data);
      } catch (e) {
        if (!cancelled) {
          toast.error(formatError(e.response?.data?.detail) || "Failed to load announcement");
          onClose?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, announcementId, onClose]);

  const canApprove = !!ann
    && ann.status === "pending"
    && (accessRole === "master_admin" || (accessRole === "admin" && !ann.important));
  const canReject = canApprove;

  const doApprove = async () => {
    if (!ann) return;
    setBusy(true);
    try {
      await api.post(`/announcements/${ann.id}/approve`);
      toast.success("Announcement approved");
      onActionDone?.("approved");
      onClose?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Approve failed");
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!ann) return;
    const r = reason.trim();
    if (r.length < 3) { toast.error("Reason (min 3 chars) required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("reason", r);
      await api.post(`/announcements/${ann.id}/reject`, fd);
      toast.success("Announcement rejected");
      onActionDone?.("rejected");
      onClose?.();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Reject failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ann-approval-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {ann?.important
              ? <AlertTriangle className="w-5 h-5 text-rose-500" />
              : <Megaphone className="w-5 h-5 text-indigo-500" />}
            {ann?.title || (loading ? "Loading…" : "Announcement")}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-slate-400 text-sm">Loading announcement…</div>
        )}

        {!loading && ann && (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={STATUS_TONE[ann.status] || "bg-slate-200 text-slate-700"}>
                {ann.status}
              </Badge>
              {ann.important && (
                <Badge className="bg-rose-100 text-rose-800 border-rose-200">
                  Important · mandatory acknowledgement
                </Badge>
              )}
              {ann.auto_approved && (
                <Badge className="bg-slate-100 text-slate-700 border-slate-200">
                  <Zap className="w-3 h-3 mr-1" /> Self-Approved
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-[13px]">
              <div><span className="text-slate-400">Created by:</span> <strong>{ann.created_by_name}</strong></div>
              <div><span className="text-slate-400">Role:</span> {roleLabel(ann.created_by_role)}{ann.created_by_designation ? ` · ${ann.created_by_designation}` : ""}</div>
              <div><span className="text-slate-400">Created at:</span> {fmtDateTimeIST(ann.created_at)}</div>
              <div><span className="text-slate-400">Ends at:</span> {fmtDateTimeIST(ann.end_at)}</div>
              {ann.scheduled_for && (
                <div><span className="text-slate-400">Scheduled for:</span> {fmtDateTimeIST(ann.scheduled_for)}</div>
              )}
              {ann.important && ann.status === "pending" && (
                <div className="col-span-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-rose-900">
                  Important announcements require <strong>Master Admin approval</strong> — they will NOT auto-post.
                </div>
              )}
              {ann.status === "rejected" && ann.rejection_reason && (
                <div className="col-span-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-rose-900">
                  <div className="font-medium mb-0.5">Rejected by {ann.rejected_by_name}</div>
                  <div className="text-[12px] opacity-80">Reason: {ann.rejection_reason}</div>
                </div>
              )}
              {ann.status === "approved" && ann.approved_by_name && (
                <div className="col-span-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-emerald-900">
                  <div className="font-medium">Approved by {ann.approved_by_name}</div>
                  {ann.approved_at && <div className="text-[12px] opacity-80">{fmtDateTimeIST(ann.approved_at)}</div>}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 whitespace-pre-wrap text-slate-800" data-testid="ann-approval-body">
              {ann.body || <span className="text-slate-400">No body.</span>}
            </div>

            {ann.external_url && (
              <a href={ann.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-emerald-700 hover:underline">
                <ExternalLink className="w-4 h-4" /> {ann.external_url}
              </a>
            )}

            {ann.attachment_id && (
              <a
                href={`${BACKEND}/api/announcements/${ann.id}/attachment`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 hover:bg-slate-50"
                data-testid="ann-approval-attachment"
              >
                <Download className="w-4 h-4" /> {ann.attachment_name || "Attachment"}
              </a>
            )}

            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-[12px] uppercase text-slate-400 mb-2 flex items-center gap-1">
                <Users className="w-3 h-3" /> Audience
              </div>
              {ann.target_all ? (
                <Badge className="bg-emerald-100 text-emerald-800">All Employees</Badge>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {(ann.target_roles || []).map((r) => (
                    <Badge key={r} variant="secondary" className="bg-slate-100 text-slate-700">{roleLabel(r)}</Badge>
                  ))}
                  {(ann.target_user_names || []).map((n, i) => (
                    <Badge key={i} variant="secondary" className="bg-blue-50 text-blue-800">{n}</Badge>
                  ))}
                  {!(ann.target_roles?.length || ann.target_user_names?.length) && (
                    <span className="text-slate-400 text-[12px]">—</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-wrap gap-2">
          {canApprove && !rejectMode && (
            <>
              <Button
                onClick={doApprove}
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="ann-approval-approve-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
              </Button>
              <Button
                variant="destructive"
                onClick={() => setRejectMode(true)}
                disabled={busy}
                data-testid="ann-approval-reject-btn"
              >
                <XCircle className="w-4 h-4 mr-1.5" /> Reject
              </Button>
            </>
          )}
          {canReject && rejectMode && (
            <div className="w-full space-y-2">
              <Label>Rejection reason</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                placeholder="Explain why this is being rejected"
                data-testid="ann-approval-reject-reason"
                autoFocus
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setRejectMode(false); setReason(""); }} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={doReject}
                  disabled={busy || reason.trim().length < 3}
                  data-testid="ann-approval-reject-confirm"
                >
                  Confirm Reject
                </Button>
              </div>
            </div>
          )}
          {!canApprove && ann && (
            <div className="text-[11.5px] text-slate-500 italic mr-auto">
              {ann.status !== "pending"
                ? `Already ${ann.status} — no action needed.`
                : (ann.important && accessRole !== "master_admin"
                    ? "Only Master Admin can approve Important announcements."
                    : "You are not authorised to approve this announcement.")}
            </div>
          )}
          <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
